import React, { Suspense, useCallback, useEffect, useMemo, useState } from 'react';

import { allTransition, useCalculatedWidthV2 } from '@lyearn/lygo-blocks';
import { useNodePerformanceContext, useReactNodeView } from '@lyearn/lygo-core';
import { cn } from '@lyearn/style';
import { Box } from '@lyearn/ui';
import Loader from '@/components/Loader';
import { ScormViewerMode } from '@/modules/corporate/modules/scorm/views/ScormViewer';
import dynamic from '@/utils/dynamic';

import { useCourse } from '../../courseContext';
import CourseEntity from '../../entity/Course';
import { SCORM_ASPECT_RATIO } from './ScormCardEditable';
import { nodeContainerWidth, scormCardContainer } from './styles';
import { ScormCardContainerProps } from './types';

const ScormContainerLoadale = dynamic(
  () =>
    import(
      /* webpackChunkName: "scorm-container" */ '@/modules/corporate/modules/scorm/views/ScormViewer/ScormEmbed'
    ),
);

const ScormCardReadOnly: React.FC<ScormCardContainerProps> = (props) => {
  const { scormId, width, height } = props;
  const [scormFullscreen, setScormFullscreen] = useState(false);

  const { course } = useCourse();

  const calculatedWidth = useCalculatedWidthV2(width as number, true);
  const calculatedHeight = height ? height : SCORM_ASPECT_RATIO * calculatedWidth;

  const parentContentId = CourseEntity.id(course);

  const { setNodePerformance, getNodePerformance } = useNodePerformanceContext();
  const { node } = useReactNodeView();

  const nodePerformance = useMemo(
    () => getNodePerformance?.(node?.attrs.id),
    [getNodePerformance, node?.attrs.id],
  );

  const setScormPerformance = useCallback(
    (progress: number) => {
      setNodePerformance?.({
        id: node?.attrs.id,
        blockType: 'ly__scorm',
        progress,
      });
    },
    [node?.attrs.id, setNodePerformance],
  );

  useEffect(() => {
    // set performance for empty block where scorm is still to be attached
    if (!scormId && node?.attrs.id && nodePerformance?.progress !== 100) {
      setScormPerformance(100);
    }
  }, [scormId, node?.attrs.id, setScormPerformance, nodePerformance?.progress]);

  if (!parentContentId) {
    return null;
  }

  return (
    <Box className={cn('relative my-32', { 'pointer-events-none': !CourseEntity.id(course) })}>
      {scormId ? (
        <Suspense fallback={<Loader />}>
          <Box
            className={cn(
              'relative xs:end-0 xs:start-0',
              {
                '-translate-x-1/2 start-1/2 rtl:translate-x-1/2 xs:translate-x-0 ':
                  !scormFullscreen,
              },
              allTransition,
              scormCardContainer,
              nodeContainerWidth(calculatedWidth, calculatedHeight),
            )}>
            <ScormContainerLoadale
              contentId={scormId}
              editable={false}
              languageType={course.languageType}
              mode={ScormViewerMode.Embed}
              parentContentId={parentContentId}
              onFullScreenChange={setScormFullscreen}
            />
          </Box>
        </Suspense>
      ) : null}
    </Box>
  );
};

export default React.memo(ScormCardReadOnly);
